import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

type MissionPillar = {
  title: string;
  bullets?: string[];
};

export const MissionPillars = () => {
  const { t } = useTranslation();

  const title = t("about.sections.mission.title", "Our Mission");
  const subtitle = t("about.sections.mission.subtitle", "");
  const pillars = t("about.sections.mission.pillars", { returnObjects: true }) as MissionPillar[];
  const items = Array.isArray(pillars) ? pillars : [];

  return (
    <section id="mission" className="py-24 bg-neutral-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, amount: 0.5 }}
          className="max-w-3xl text-center md:text-left mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900">
            {title}
          </h2>
          {subtitle ? (
            <p className="mt-3 text-lg text-neutral-600">{subtitle}</p>
          ) : null}
        </motion.div>

        <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-4">
          {items.map((pillar, index) => (
            <motion.article
              key={`${pillar.title}-${index}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.05 }}
              viewport={{ once: true, amount: 0.4 }}
              className="flex h-full flex-col rounded-3xl border border-neutral-200 bg-white p-6 shadow-sm"
            >
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-turquoise">
                {String(index + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-3 text-xl font-semibold text-neutral-900">
                {pillar.title}
              </h3>
              {pillar.bullets?.length ? (
                <ul className="mt-4 space-y-3 text-sm text-neutral-600">
                  {pillar.bullets.map((bullet, bulletIndex) => (
                    <li key={bulletIndex} className="flex gap-2">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-turquoise" aria-hidden />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
